import React from 'react';
import { Box, Tooltip } from '@mui/material';
import { GridDataSource, GridColDef, useGridApiRef, GridRowModesModel, GridDataSourceCache, GridGetRowsParams, GridGetRowsResponse, GridDataSourceCacheDefault, GridRenderCellParams } from '@mui/x-data-grid';
import { formatNumber } from '../../Common/FormatNumber';
import CustomDataGrid from '../../Common/CustomDataGrid';

export default function WhisperInterest({ perm, mode, height }: { perm?: string; mode: string; height: number }){
    const apiRef = useGridApiRef();
    const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>({});
    const [columns, setColumns] = React.useState<GridColDef[]>([
        {
            field: 'entity_cust_id',
            headerName: 'Cust ID',
            renderHeader: (params) => {
                return <span style={{ fontWeight: 'bold' }}>{params.colDef.headerName as string}</span>;
            },
            flex: 0.5,
            align: 'center',
            headerAlign: 'center',
            type: 'string',
            renderCell: (params: GridRenderCellParams) => {
                return <span style={{ color: params.row.color }}>{params.value}</span>;
            },
        },
        {
            field: 'customer_name',
            headerName: 'Customer',
            renderHeader: (params) => {
                return <span style={{ fontWeight: 'bold' }}>{params.colDef.headerName as string}</span>;
            },
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            type: 'string',
            filterable: true,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Tooltip id={`tooltip-${params.row.id}`} title={params.row.whisper}>
                        <span style={{ color: 'blue', borderBottom: '1px dashed blue', cursor: 'pointer' }}>{params.value}</span>
                    </Tooltip>
                )
            },
        },
        {
            field: 'channel_descr',
            headerName: 'Condition',
            renderHeader: (params) => {
                return <span style={{ fontWeight: 'bold' }}>{params.colDef.headerName as string}</span>;
            },
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            type: 'string',
            renderCell: (params: GridRenderCellParams) => {
                return <span style={{ color: params.row.color }}>{params.value}</span>;
            },
        },
        {
            field: 'condition',
            headerName: 'Last Months Sales',
            renderHeader: (params) => {
                return <span style={{ textAlign: 'center', fontWeight: 'bold' }}>Last Months <br/> Sales</span>;
            },
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            type: 'number',
            filterable: false,
            renderCell: (params: GridRenderCellParams) => {
                return <span>{formatNumber(params.value)}</span>;
            },
        },
        {
            field: 'control',
            headerName: 'Monthly Average',
            renderHeader: (params) => {
                return <span style={{ textAlign: 'center', fontWeight: 'bold' }}>Monthly <br/> Average</span>;
            },
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            type: 'number',
            filterable: false,
            renderCell: (params: GridRenderCellParams) => {
                return <span>{formatNumber(params.value)}</span>;
            },
        },
        {
            field: 'difference',
            headerName: 'Difference',
            renderHeader: (params) => {
                return <span style={{ fontWeight: 'bold' }}>{params.colDef.headerName as string}</span>;
            },
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            type: 'number',
            filterable: false,
            renderCell: (params: GridRenderCellParams) => {
                return <span style={{ color: params.row.color }}>{formatNumber(params.row.condition - params.row.control)}</span>;
            },
        }
    ]);

    const gridDataSource: GridDataSource = {
        getRows: async (params: GridGetRowsParams): Promise<GridGetRowsResponse> => {
            let urlDict = {};

            urlDict = {paginationModel: JSON.stringify(params.paginationModel)};
            if (params.filterModel.items.length > 0) {
                urlDict = { ...urlDict, filterModel: JSON.stringify(params.filterModel) };
            }
            if (params.sortModel.length > 0) {
                urlDict = { ...urlDict, sortModel: JSON.stringify(params.sortModel) };
            }

            const urlParams = new URLSearchParams(urlDict);
            const permParam = perm ? `&perm=${perm}` : '';
            const urlParamsString = urlParams.size > 0
                ? `?${urlParams.toString()}${permParam}&entity_id=${localStorage.getItem('entity_id')}`
                : `?entity_id=${localStorage.getItem('entity_id')}${permParam}`;
            const response = await fetch(`/dashboard/interests/${urlParamsString}`, {
                method: 'GET',
            });
            const data = await response.json();
            return {
                rows: data.rows,
                rowCount: data.rowCount,
            };
        },
    }

    const [lowTTLCache, setLowTTLCache] = React.useState<GridDataSourceCache>(() => new GridDataSourceCacheDefault({ ttl: 1000 * 10 }));

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%', borderRadius: '6px !important', backgroundColor: 'white', border: '1px solid #f0f0f0 !important'}}>
            <Box sx={{ width: '100%', typography: 'body1', height: '100%', margin: 0, padding: 0 }}>
                <CustomDataGrid
                    message="No whisper interests found."
                    mode={mode}
                    columns={columns}
                    gridDataSource={gridDataSource}
                    sortModel={[]}
                    apiRef={apiRef}
                    rowModesModel={rowModesModel}
                    lowTTLCache={lowTTLCache}
                    navigate={true}
                    defineMaxHeight={height}
                    title="Monthly Concerns"
                    url={`/dashboard/interests/`}
                    exportUrl={`/${perm || 'admin'}/whisper-interest/export/`}
                />
            </Box>
        </Box>
    )
}
